import { useMemo } from 'react';

import type { BlockFeature } from '~api/services';
import type { CoordinateTransformer, Point } from '~types';
import { type ColourScale, polygonCenterToSVG } from '~utils';
import { Label } from '../components/Label';
import { useSvgContext } from '../components/SvgContext';
import { VISUALIZATION_CONFIG } from '../config';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type BlockLabel = {
  /** SVG x-coordinate for the label */
  x: number;
  /** SVG y-coordinate for the label */
  y: number;
  /** Display text from the colour scale legend */
  text: string;
  /** Fill colour for the text (uses stroke from colour scale for contrast) */
  fill: string;
};

type Cluster = { cluster: BlockFeature[]; anchor: BlockFeature };

/** Shared state passed to the cluster traversal so it can walk the spatial grid without reallocating per cluster. */
type ClusterTraversalContext = {
  /** All same-colour blocks being clustered */
  blocks: BlockFeature[];
  /** Spatial hash grid mapping cell key -> block indices */
  grid: Map<string, number[]>;
  /** Grid cell size (also the adjacency threshold) */
  cellSize: number;
  /** Squared adjacency threshold, precomputed to skip Math.sqrt in distance checks */
  thresholdSq: number;
  /** Tracks which blocks have already been assigned to a cluster (mutated across traversal calls) */
  visited: boolean[];
};

type BlockLabelsLayerProps = {
  /** Block features to compute labels for */
  blocks?: BlockFeature[];
  /** Active colour scale (from "Colour Blocks By" selector) */
  colourScale?: ColourScale;
};

/* -------------------------------------------------------------------------- */
/*                                   Constants                                */
/* -------------------------------------------------------------------------- */

/**
 * Multiplier of block dimension used as the adjacency threshold. `1.2` gives 4-connectivity
 * (side-adjacent blocks only, ~1.0x side distance) while excluding diagonal-only neighbours
 * (~1.414x side distance). This matches the mining convention that corner-touching blocks
 * are treated as separate groups, so each gets its own label rather than being merged.
 */
const ADJACENCY_MULTIPLIER = 1.2;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const cellKey = (x: number, y: number, size: number): string => `${Math.floor(x / size)},${Math.floor(y / size)}`;

/**
 * Builds a spatial hash grid mapping cell key -> block indices for O(1) neighbourhood lookup.
 */
const buildGrid = (blocks: BlockFeature[], cellSize: number): Map<string, number[]> => {
  const grid = new Map<string, number[]>();
  for (let i = 0; i < blocks.length; i += 1) {
    const { xcentre, ycentre } = blocks[i].properties;
    const key = cellKey(xcentre, ycentre, cellSize);
    const bucket = grid.get(key);
    if (bucket) bucket.push(i);
    else grid.set(key, [i]);
  }
  return grid;
};

/** 3x3 offsets for scanning a cell's neighbourhood in the spatial grid. */
const CELL_OFFSETS: readonly [number, number][] = [
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, -1],
  [0, 0],
  [0, 1],
  [1, -1],
  [1, 0],
  [1, 1],
];

/**
 * Returns indices of blocks within the adjacency threshold of `blocks[i]`, using the 3x3
 * grid neighbourhood around its cell so lookup is O(1) amortised per block.
 */
const findNeighbours = (
  i: number,
  blocks: BlockFeature[],
  grid: Map<string, number[]>,
  cellSize: number,
  thresholdSq: number,
): number[] => {
  const { xcentre, ycentre } = blocks[i].properties;
  const cx = Math.floor(xcentre / cellSize);
  const cy = Math.floor(ycentre / cellSize);

  return CELL_OFFSETS.flatMap(([dx, dy]) => grid.get(`${cx + dx},${cy + dy}`) ?? []).filter((j) => {
    if (j === i) return false;
    const other = blocks[j].properties;
    const ddx = xcentre - other.xcentre;
    const ddy = ycentre - other.ycentre;
    return ddx * ddx + ddy * ddy <= thresholdSq;
  });
};

/**
 * Multi-source BFS from edge blocks. Returns each block's grid distance from the nearest
 * edge (fully-interior blocks are the deepest); this is the discrete distance-transform
 * used to find the pole-of-inaccessibility on a block grid.
 */
const computeEdgeDistances = (adjacency: number[][]): { depths: number[]; maxDepth: number } => {
  let maxNeighbours = 0;
  for (const nbrs of adjacency) if (nbrs.length > maxNeighbours) maxNeighbours = nbrs.length;

  const depths = new Array<number>(adjacency.length).fill(-1);
  const queue: number[] = [];
  for (let i = 0; i < adjacency.length; i += 1) {
    if (adjacency[i].length < maxNeighbours) {
      depths[i] = 0;
      queue.push(i);
    }
  }

  let maxDepth = 0;
  for (let head = 0; head < queue.length; head += 1) {
    const i = queue[head];
    for (const j of adjacency[i]) {
      if (depths[j] !== -1) continue;
      depths[j] = depths[i] + 1;
      if (depths[j] > maxDepth) maxDepth = depths[j];
      queue.push(j);
    }
  }
  return { depths, maxDepth };
};

/** Geometric centroid of a set of blocks in world coordinates. */
const clusterCentroid = (cluster: BlockFeature[]): { x: number; y: number } => {
  let sumX = 0;
  let sumY = 0;
  for (const { properties } of cluster) {
    sumX += properties.xcentre;
    sumY += properties.ycentre;
  }
  return { x: sumX / cluster.length, y: sumY / cluster.length };
};

/**
 * Picks the anchor block using a grid distance-transform: the block furthest from any
 * cluster edge wins — this is the pole-of-inaccessibility on the block grid, the same
 * principle polylabel uses for polygons, adapted to gridded data. Ties are broken by
 * proximity to the cluster centroid for stable, visually centred placement.
 */
const pickAnchor = (cluster: BlockFeature[], adjacency: number[][]): BlockFeature => {
  if (cluster.length === 1) return cluster[0];

  const { depths, maxDepth } = computeEdgeDistances(adjacency);
  // Degenerate case: no edges detected (e.g. all blocks equally connected).
  if (depths.every((d) => d === -1)) return cluster[0];

  const { x: cx, y: cy } = clusterCentroid(cluster);

  let anchor = cluster[0];
  let bestDistSq = Infinity;
  for (let i = 0; i < cluster.length; i += 1) {
    if (depths[i] !== maxDepth) continue;
    const dx = cluster[i].properties.xcentre - cx;
    const dy = cluster[i].properties.ycentre - cy;
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      anchor = cluster[i];
    }
  }
  return anchor;
};

/**
 * BFS flood-fill starting at `start`. Builds the connected cluster along with its
 * cluster-local adjacency lists (needed for the pole-of-inaccessibility anchor search).
 */
const traverseCluster = (start: number, ctx: ClusterTraversalContext): Cluster => {
  const { blocks, grid, cellSize, thresholdSq, visited } = ctx;
  visited[start] = true;

  const queue = [start];
  const cluster: BlockFeature[] = [blocks[start]];
  const localIndex = new Map<number, number>([[start, 0]]);
  const adjacency: number[][] = [[]];

  for (let head = 0; head < queue.length; head += 1) {
    const globalIdx = queue[head];
    const neighbours = findNeighbours(globalIdx, blocks, grid, cellSize, thresholdSq);

    for (const j of neighbours) {
      if (!visited[j]) {
        visited[j] = true;
        localIndex.set(j, cluster.length);
        cluster.push(blocks[j]);
        adjacency.push([]);
        queue.push(j);
      }
      const jLocal = localIndex.get(j) as number;
      adjacency[head].push(jLocal);
    }
  }

  return { cluster, anchor: pickAnchor(cluster, adjacency) };
};

/**
 * Splits same-colour blocks into spatially connected clusters via BFS flood-fill.
 * Each cluster is paired with its anchor block (the most-connected block, which sits
 * at the densest, most visually central part). O(n) amortised via spatial grid.
 */
const clusterAndAnchor = (blocks: BlockFeature[]): Cluster[] => {
  if (blocks.length === 0) return [];

  // Derive cell size from the largest block in the group so mixed sub-block sizes
  // (common when sub-blocking or multiple benches share a property value) still
  // cluster correctly; using blocks[0] alone can silently split or merge clusters.
  let maxDim = 0;
  for (const { properties } of blocks) {
    if (properties.xlength > maxDim) maxDim = properties.xlength;
    if (properties.ylength > maxDim) maxDim = properties.ylength;
  }
  const cellSize = maxDim * ADJACENCY_MULTIPLIER;
  const ctx: ClusterTraversalContext = {
    blocks,
    grid: buildGrid(blocks, cellSize),
    cellSize,
    thresholdSq: cellSize * cellSize,
    visited: new Array<boolean>(blocks.length).fill(false),
  };

  const clusters: Cluster[] = [];
  for (let start = 0; start < blocks.length; start += 1) {
    if (!ctx.visited[start]) clusters.push(traverseCluster(start, ctx));
  }

  return clusters;
};

/**
 * Groups blocks by their discrete colour-scale property, splits each group into spatially
 * connected clusters, and produces one label per cluster. Returns an empty array for
 * continuous colour scales (numeric gradients have no meaningful text labels).
 */
const computeBlockLabels = (
  blocks: BlockFeature[],
  colourScale: ColourScale,
  transformer: CoordinateTransformer,
): BlockLabel[] => {
  if (colourScale.type !== 'discrete') return [];

  const groups = new Map<string, BlockFeature[]>();
  for (const block of blocks) {
    const value = String(block.properties[colourScale.property as keyof typeof block.properties] ?? '');
    const bucket = groups.get(value);
    if (bucket) bucket.push(block);
    else groups.set(value, [block]);
  }

  const labels: BlockLabel[] = [];
  for (const [value, groupBlocks] of groups) {
    const text = colourScale.legend.find((l) => l.value === value)?.label ?? value;
    const fill = colourScale.getColour(value).stroke;

    for (const { anchor } of clusterAndAnchor(groupBlocks)) {
      // Use polygonCenterToSVG (same helper as DrillDomainsLayer) so labels land at the
      // anchor block's pole-of-inaccessibility in geographic space, not a corner coord.
      const coords = anchor.geometry.coordinates[0] as Point[];
      const center = polygonCenterToSVG(coords, transformer);
      if (!center) continue;
      labels.push({ x: center.x, y: center.y, text, fill });
    }
  }

  return labels;
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Renders one label per spatially connected cluster of same-colour blocks.
 * Only shows for discrete colour scales; numeric gradients render nothing.
 *
 * @example
 * ```tsx
 * <BlockLabelsLayer blocks={blocks} colourScale={blockColourScale} />
 * ```
 */
const BlockLabelsLayer = ({ blocks, colourScale }: BlockLabelsLayerProps) => {
  const { transformer } = useSvgContext();

  const labels = useMemo(() => {
    if (!blocks?.length || !colourScale || !transformer) return [];
    return computeBlockLabels(blocks, colourScale, transformer);
  }, [blocks, colourScale, transformer]);

  if (!labels.length) return null;

  const { FONT_SIZE, FONT_WEIGHT } = VISUALIZATION_CONFIG.BLOCK_LABELS;

  return (
    <g className="block-labels">
      {labels.map(({ x, y, text, fill }) => (
        <Label
          key={`${text}-${x}-${y}`}
          x={x}
          y={y}
          text={text}
          fill={fill}
          fontSize={FONT_SIZE}
          fontWeight={FONT_WEIGHT}
        />
      ))}
    </g>
  );
};
BlockLabelsLayer.displayName = 'BlockLabelsLayer';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { BlockLabelsLayer, type BlockLabelsLayerProps, computeBlockLabels };
